import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, MessageSquare, Users, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';
import MobileLandingView from '@/components/MobileLandingView';

interface FeatureItemProps {
  title: string;
  description: string;
  delay: number;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ title, description, delay }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  return (
    <motion.div
      className={cn(
        "flex flex-col items-center p-6 rounded-xl border", 
        isDark 
          ? "bg-[#151830] border-[#252a47] hover:border-[#6C41E0]/50" 
          : "bg-white border-slate-200/50 hover:border-blue-300/50 shadow-sm"
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      whileHover={{ y: -5, boxShadow: isDark ? '0 10px 30px rgba(0, 0, 0, 0.2)' : '0 10px 30px rgba(0, 0, 0, 0.05)' }}
    >
      <h3 className={cn(
        "text-lg font-semibold mb-2",
        isDark ? "text-white" : "text-slate-900"
      )}>
        {title}
      </h3>
      <p className={cn(
        "text-center",
        isDark ? "text-white/70" : "text-slate-600"
      )}>
        {description}
      </p>
    </motion.div>
  );
};

interface StatItemProps {
  value: string | number;
  label: string;
  icon: React.ReactNode;
  color: string;
  delay: number;
}

const StatItem: React.FC<StatItemProps> = ({ value, label, icon, color, delay }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  return (
    <motion.div 
      className={cn(
        "flex items-center gap-4 px-5 py-3 rounded-full",
        isDark ? "bg-[#151830]" : "bg-white shadow-sm border border-slate-200/50"
      )}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.4 }}
      whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
    >
      <div className={`w-10 h-10 flex items-center justify-center rounded-full ${color}`}>
        {icon}
      </div>
      <div className="flex flex-col">
        <span className={cn(
          "text-lg font-semibold",
          isDark ? "text-white" : "text-slate-900"
        )}>
          {value}
        </span>
        <span className={cn(
          "text-xs",
          isDark ? "text-white/60" : "text-slate-500"
        )}>
          {label}
        </span>
      </div>
    </motion.div>
  );
};

const Index = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isMobile = useIsMobile();
  
  const [stats, setStats] = useState({
    onlineUsers: 1425,
    activeRooms: 87,
    totalChats: 3251
  });
  
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        onlineUsers: prev.onlineUsers + Math.floor(Math.random() * 3),
        activeRooms: prev.activeRooms + Math.floor(Math.random() * 2),
        totalChats: prev.totalChats + Math.floor(Math.random() * 5)
      }));
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    size: Math.floor(Math.random() * 100) + 50,
    x: `${Math.random() * 100}%`,
    y: `${Math.random() * 100}%`,
    duration: Math.floor(Math.random() * 20) + 10,
    delay: Math.random() * 5
  }));
  
  if (isMobile) {
    return (
      <div className={cn(
        "min-h-screen flex flex-col relative overflow-hidden",
        isDark ? "bg-[#0a0e1e] text-white" : "bg-white text-slate-900"
      )}>
        {particles.map((particle) => (
          <motion.div 
            key={particle.id}
            className={cn(
              "absolute rounded-full z-0",
              isDark ? "bg-blue-500/5" : "bg-blue-500/3"
            )}
            style={{ 
              width: particle.size / 2, 
              height: particle.size / 2, 
              left: particle.x, 
              top: particle.y 
            }}
            animate={{
              y: [0, -15, 0],
              opacity: [0.5, 0.8, 0.5],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
              ease: "easeInOut"
            }}
          />
        ))}
        
        <MobileLandingView stats={stats} />
      </div>
    );
  }
  
  return (
    <div className={cn(
      "min-h-screen flex flex-col relative overflow-hidden",
      isDark ? "bg-[#0a0e1e] text-white" : "bg-white text-slate-900"
    )}>
      {particles.map((particle) => (
        <motion.div 
          key={particle.id}
          className={cn(
            "absolute rounded-full z-0",
            isDark ? "bg-blue-500/5" : "bg-blue-500/3"
          )}
          style={{ 
            width: particle.size, 
            height: particle.size, 
            left: particle.x, 
            top: particle.y 
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: "easeInOut"
          }}
        />
      ))}
      
      <section className="flex-1 flex flex-col items-center justify-center px-6 text-center py-16 md:py-24 relative z-10">
        <div className="max-w-3xl mx-auto">
          <motion.div 
            className={cn(
              "inline-block py-2 px-4 rounded-full mb-6",
              isDark ? "bg-blue-500/10 text-blue-400" : "bg-blue-500/10 text-blue-600"
            )}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="text-sm font-medium">Connect Instantly, Chat Freely</span>
          </motion.div>
          
          <motion.h1 
            className={cn(
              "text-4xl md:text-6xl font-bold tracking-tight mb-4",
              isDark ? "text-white" : "text-slate-900"
            )}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Meet New People <br/>
            <span className="bg-gradient-to-r from-blue-400 to-violet-500 bg-clip-text text-transparent">
              From Anywhere, Anytime
            </span>
          </motion.h1>
          
          <motion.p 
            className={cn(
              "text-lg md:text-xl mb-8 md:mb-10 max-w-2xl mx-auto",
              isDark ? "text-white/70" : "text-slate-700"
            )}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Chatrix connects you with interesting people worldwide in just seconds.
          </motion.p>
          
          <motion.div
            className="flex flex-wrap justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Link to="/chat">
              <Button 
                size="lg" 
                className={cn(
                  "bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white px-8 py-6 h-auto rounded-full shadow-lg group",
                  isDark ? "shadow-blue-900/30" : "shadow-blue-500/20"
                )}
              >
                Start Video Chat
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
        
        <motion.div 
          className="w-full max-w-2xl mx-auto mt-16 flex flex-wrap justify-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <StatItem 
            value={stats.onlineUsers.toLocaleString()}
            label="Online Now"
            icon={<Users className="h-5 w-5 text-white" />}
            color="bg-gradient-to-br from-green-500 to-green-600"
            delay={0.7}
          />
          
          <StatItem 
            value={stats.activeRooms.toLocaleString()}
            label="Active Rooms"
            icon={<MessageSquare className="h-5 w-5 text-white" />}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
            delay={0.8}
          />
          
          <StatItem 
            value={stats.totalChats.toLocaleString()}
            label="Chats Today"
            icon={<MessageSquare className="h-5 w-5 text-white" />}
            color="bg-gradient-to-br from-violet-500 to-violet-600"
            delay={0.9}
          />
        </motion.div>
      </section>
      
      <section className={cn(
        "w-full py-16 px-4",
        isDark ? "bg-[#0a0e1e]" : "bg-white"
      )}>
        <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h2 
            className={cn(
              "text-2xl md:text-3xl font-bold text-center mb-8",
              isDark ? "text-white" : "text-slate-900"
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <span className="bg-gradient-to-r from-blue-400 to-violet-500 bg-clip-text text-transparent">Secure & Private</span> Connections
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <motion.img 
                src="/images/privacy-3d.svg" 
                alt="Privacy Shield" 
                className="w-full h-auto max-w-[200px] mx-auto md:max-w-full"
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <div className="md:col-span-2 flex flex-col justify-center">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className={cn(
                  "p-4 md:p-6 rounded-xl border", 
                  isDark 
                    ? "bg-[#151830] border-[#252a47]" 
                    : "bg-white border-slate-200/50 shadow-sm"
                )}
              >
                <div className="flex items-center mb-3">
                  <h3 className={cn(
                    "text-lg md:text-xl font-semibold",
                    isDark ? "text-white" : "text-slate-900"
                  )}>
                    Your Privacy is Our Priority
                  </h3>
                </div>
                <p className={cn(
                  "text-sm md:text-base",
                  isDark ? "text-white/70" : "text-slate-600"
                )}>
                  All conversations on Chatrix are protected with end-to-end encryption. 
                  Your personal data never leaves your device without your explicit permission.
                </p>
                <div className="mt-3 md:mt-4 flex flex-wrap gap-2">
                  
                  
                </div>
              </motion.div>
            </div>
          </div>
          
          <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 flex flex-col justify-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
                className={cn(
                  "p-4 md:p-6 rounded-xl border", 
                  isDark 
                    ? "bg-[#151830] border-[#252a47]" 
                    : "bg-white border-slate-200/50 shadow-sm"
                )}
              >
                <div className="flex items-center mb-3">
                  <h3 className={cn(
                    "text-lg md:text-xl font-semibold",
                    isDark ? "text-white" : "text-slate-900"
                  )}>
                    Lightning Fast Connections
                  </h3>
                </div>
                <p className={cn(
                  "text-sm md:text-base",
                  isDark ? "text-white/70" : "text-slate-600"
                )}>
                  Our advanced matching algorithm pairs you with interesting people in seconds.
                  With servers distributed globally, enjoy minimal latency no matter where you are.
                </p>
                <div className="mt-3 md:mt-4 flex flex-wrap gap-2">
                  
                </div>
              </motion.div>
            </div>
            <div className="md:col-span-1">
              <motion.img 
                src="/images/instant-3d.svg" 
                alt="Instant Connections" 
                className="w-full h-auto max-w-[200px] mx-auto md:max-w-full"
                whileHover={{ scale: 1.05, rotate: -5 }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
          
          <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <motion.img 
                src="/images/global-3d.svg" 
                alt="Global Reach" 
                className="w-full h-auto max-w-[200px] mx-auto md:max-w-full"
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <div className="md:col-span-2 flex flex-col justify-center">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
                className={cn(
                  "p-4 md:p-6 rounded-xl border", 
                  isDark 
                    ? "bg-[#151830] border-[#252a47]" 
                    : "bg-white border-slate-200/50 shadow-sm"
                )}
              >
                <div className="flex items-center mb-3">
                  <h3 className={cn(
                    "text-lg md:text-xl font-semibold",
                    isDark ? "text-white" : "text-slate-900"
                  )}>
                    Worldwide Connections
                  </h3>
                </div>
                <p className={cn(
                  "text-sm md:text-base",
                  isDark ? "text-white/70" : "text-slate-600"
                )}>
                  Meet people from different cultures and backgrounds.
                  Our built-in translation features help break down language barriers,
                  letting you connect with anyone, anywhere.
                </p>
                <div className="mt-3 md:mt-4 flex flex-wrap gap-2">
                  
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </section>
      
      <section className={cn(
        "w-full py-16 px-4",
        isDark ? "bg-[#0a0e1e]" : "bg-white"
      )}>
        <motion.div 
          className={cn(
            "w-full max-w-4xl mx-auto shadow-2xl rounded-xl overflow-hidden border",
            isDark ? "border-[#252a47]" : "border-slate-200/50"
          )}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          whileHover={{ y: -5, boxShadow: isDark ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : '0 25px 50px -12px rgba(0, 0, 0, 0.15)' }}
        >
          <div className={cn(
            "px-4 py-3 flex items-center gap-2",
            isDark ? "bg-[#151830]" : "bg-slate-100"
          )}>
            <div className="flex gap-1.5">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            </div>
            <div className={cn(
              "w-full max-w-md mx-auto rounded-full px-4 py-1 text-xs text-center",
              isDark ? "bg-[#252a47] text-white/60" : "bg-slate-200 text-slate-600"
            )}>
              chatrix.app
            </div>
          </div>
          <div className="relative">
            <div className={cn(
              "h-60 md:h-80 flex items-center justify-center",
              isDark 
                ? "bg-gradient-to-b from-[#151830] to-black" 
                : "bg-gradient-to-b from-slate-100 to-slate-300"
            )}>
              <div className={isDark ? "text-white/70" : "text-slate-700"}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2, duration: 0.5 }}
                >
                  <p className="text-base md:text-xl mb-1 md:mb-2 font-medium">Connect with people worldwide</p>
                  <p className="text-xs md:text-sm">Make new friends in seconds</p>
                </motion.div>
              </div>
            </div>
            
            <motion.div 
              className={cn(
                "absolute bottom-4 right-4 w-48 md:w-64 h-36 md:h-48 rounded-lg overflow-hidden border shadow-lg",
                isDark ? "border-white/20" : "border-slate-300"
              )}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.4, duration: 0.5 }}
            >
              <div className={cn(
                "h-full p-2 md:p-3 flex flex-col",
                isDark ? "bg-[#0D0F21]" : "bg-white"
              )}>
                <div className={cn(
                  "text-xs text-center mb-1 md:mb-2",
                  isDark ? "text-white/70" : "text-slate-500"
                )}>
                  Chat
                </div>
                <div className="flex-1 overflow-y-auto space-y-2">
                  <div className={cn(
                    "text-white text-xs p-1.5 md:p-2 rounded-lg rounded-tl-none max-w-[80%]",
                    isDark ? "bg-[#292D67]" : "bg-blue-500"
                  )}>
                    Hello there! How are you doing today?
                  </div>
                  <div className={cn(
                    "text-white text-xs p-1.5 md:p-2 rounded-lg rounded-tr-none max-w-[80%] ml-auto",
                    "bg-gradient-to-r from-[#5B35C0] to-[#6C41E0]"
                  )}>
                    Hi! I'm doing great, thanks for asking!
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className={cn(
                "absolute bottom-4 left-1/2 transform -translate-x-1/2 backdrop-blur-md rounded-full px-3 md:px-4 py-1.5 md:py-2 flex gap-2 md:gap-3 border",
                isDark 
                  ? "bg-[#0D0F21]/90 border-[#6C41E0]/20" 
                  : "bg-white/90 border-slate-300/50"
              )}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.5 }}
            >
              <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-[#6C41E0]">
                <MessageSquare className="w-3 h-3 md:w-4 md:h-4 text-white" />
              </div>
              <div className={cn(
                "w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full",
                isDark ? "bg-[#30324E]" : "bg-slate-200"
              )}>
                <Users className={cn(
                  "w-3 h-3 md:w-4 md:h-4", 
                  isDark ? "text-white" : "text-slate-700"
                )} />
              </div>
              <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-red-600">
                <Check className="w-3 h-3 md:w-4 md:h-4 text-white" />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>
      
      <motion.section 
        className={cn(
          "w-full py-12 md:py-16 backdrop-blur-sm",
          isDark 
            ? "bg-gradient-to-r from-blue-500/10 to-violet-500/10" 
            : "bg-gradient-to-r from-blue-50 to-violet-50"
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
      >
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className={cn(
            "text-xl md:text-4xl font-bold mb-3 md:mb-4",
            isDark ? "text-white" : "text-slate-900"
          )}>
            Ready to Start Chatting?
          </h2>
          <p className={cn(
            "mb-6 md:mb-8 max-w-xl mx-auto text-sm md:text-base",
            isDark ? "text-white/70" : "text-slate-700"
          )}>
            Jump into live video conversations with interesting people from around the world
          </p>
          
          <Link to="/chat">
            <Button 
              size={isMobile ? "default" : "lg"}
              className={cn(
                "bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white px-6 md:px-8 py-2 md:py-6 h-auto rounded-full shadow-xl group",
                isDark ? "shadow-blue-900/30" : "shadow-blue-500/20"
              )}
            >
              Start Now
              <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </motion.section>
    </div>
  );
};

export default Index;
